
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { widgetService, type WidgetParams, type CreateWidgetData } from "@/services/dashboard";

export const useGetAllWidgets = (params?: { dashboardId?: string; type?: string }) => {
    return useQuery<any, Error>({
        queryKey: ["widgets", params],
        queryFn: () => widgetService.getAllWidgets(params),
    });
};

export const useGetWidget = (id: string, options?: { enabled?: boolean }) => {
    return useQuery<any, Error>({
        queryKey: ["widget", id],
        queryFn: () => widgetService.getWidgetById(id),
        enabled: options?.enabled !== false && !!id,
    });
};

export const useGetWidgetData = (id: string, refresh = false) => {
    return useQuery<any, Error>({
        queryKey: ["widget-data", id, refresh],
        queryFn: () => widgetService.getWidgetData(id, refresh),
        enabled: !!id,
    });
};

export const useCreateWidget = () => {
    const queryClient = useQueryClient();
    return useMutation<any, Error, CreateWidgetData>({
        mutationFn: widgetService.createWidget,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["widgets"] });
            queryClient.invalidateQueries({ queryKey: ["dashboard", data?.data?.dashboardId] });
        },
    });
};

export const useUpdateWidget = () => {
    const queryClient = useQueryClient();
    return useMutation<any, Error, any>({
        mutationFn: widgetService.updateWidget,
        onSuccess: (data, variables) => {

            queryClient.invalidateQueries({ queryKey: ["widgets"] });
            queryClient.invalidateQueries({ queryKey: ["widget", variables?.id] });
            queryClient.invalidateQueries({ queryKey: ["widget-data", variables?.id] });
            queryClient.invalidateQueries({ queryKey: ["dashboard", data?.data?.dashboardId] });
        },
    });
};

export const useDeleteWidget = () => {
    const queryClient = useQueryClient();
    return useMutation<any, Error, any>({
        mutationFn: widgetService.deleteWidget,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["widgets"] });
            queryClient.invalidateQueries({ queryKey: ["dashboard", data?.data?.dashboardId] });
        },
    });
};

export const useBulkRefreshWidgets = () => {
    const queryClient = useQueryClient();
    return useMutation<any, Error, string[]>({
        mutationFn: widgetService.bulkRefreshWidgets,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["widget-data"] });
        },
    });
};

export const useGetSandboxWidget = (id: string, options?: { enabled?: boolean }) => {
    return useQuery<any, Error>({
        queryKey: ["sandbox-widget", id],
        queryFn: () => widgetService.getSandboxWidgetById(id),
        enabled: options?.enabled !== false && !!id,
    });
};

export const useGetSandboxWidgetData = (id: string) => {
    return useQuery<any, Error>({
        queryKey: ["sandbox-widget-data", id],
        queryFn: () => widgetService.getSandboxWidgetData(id),
        enabled: !!id,
    });
};

export const useMoveWidget = () => {
    const queryClient = useQueryClient();
    return useMutation<any, Error, { id: string; targetDashboardId: string }>({
        mutationFn: ({ id, targetDashboardId }) => widgetService.moveWidget(id, targetDashboardId),
        onSuccess: (data, variables) => {
            // Invalidate queries for both source and target dashboards
            queryClient.invalidateQueries({ queryKey: ["widgets"] });
            queryClient.invalidateQueries({ queryKey: ["dashboard"] }); // Invalidate all dashboard queries
        },
    });
};
